import type { OnboardingStep } from '@rio-cloud/rio-uikit/useOnboarding';
import type { IntlShape } from 'react-intl';

export const RENTAL_ONBOARDING_STEP = {
    rentalTab: '1',
    partnersTab: '2',
    addPartner: '3',
    rentalStatus: '4',
    startRental: '5',
    multiSelect: '6',
    stopRental: '7',
    unrentable: '8',
} as const;

export type RentalOnboardingStep = (typeof RENTAL_ONBOARDING_STEP)[keyof typeof RENTAL_ONBOARDING_STEP];

type PopoverType = NonNullable<OnboardingStep['popover']>;

export const getSteps: (
    intl: IntlShape,
    setActiveStep: (step: RentalOnboardingStep | null) => void
) => OnboardingStep[] = (intl, setActiveStep) => {
    const steps: Array<{ step: RentalOnboardingStep; popover: PopoverType }> = [
        {
            step: RENTAL_ONBOARDING_STEP.rentalTab,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.rental-tab.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.rental-tab.description' })}`,
                side: 'bottom',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.partnersTab,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.partners-tab.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.partners-tab.description' })}`,
                side: 'bottom',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.addPartner,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.add-partner-step.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.add-partner-step.description' })}`,
                side: 'bottom',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.rentalStatus,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.rental-status.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.rental-status.description' })}`,
                side: 'top',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.startRental,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.start-rental.title' })}`,
                description: `${intl.formatMessage(
                    { id: 'intl-msg:rental-web.onboarding.start-rental.description' },
                    { startRentalButton: intl.formatMessage({ id: 'intl-msg:rental-web.RentalButtons.startRental' }) }
                )}`,
                side: 'top',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.multiSelect,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.multi-select.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.multi-select.description' })}`,
                side: 'top',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.stopRental,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.stop-rental.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.stop-rental.description' })}`,
                side: 'top',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Next',
            },
        },
        {
            step: RENTAL_ONBOARDING_STEP.unrentable,
            popover: {
                title: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.unrentable.title' })}`,
                description: `${intl.formatMessage({ id: 'intl-msg:rental-web.onboarding.unrentable.description' })}`,
                side: 'top',
                align: 'start',
                showButtons: ['next', 'close'],
                nextBtnText: 'Done',
            },
        },
    ];

    return steps.map(({ step, popover }: { step: RentalOnboardingStep; popover: PopoverType }) => ({
        element: `[data-onboarding-walkthrough-step="${step}"]`,
        popover,
        // TODO: Replacing the class name is just a workaround to disable interaction with the highlighted elements. This should be replaced with using the disableActiveInteraction option from the driver.js API, which will be exposed in the next UIKIT version (1.8).
        onHighlightStarted: element => {
            element?.classList.add('driver-active');
            setActiveStep(step);
        },
        onDeselected: element => {
            setActiveStep(null);
            element?.classList.remove('driver-active');
        },
        onHighlighted: element => {
            element?.classList.remove('driver-active-element');
        },
    }));
};
